import { Bookmark, Camera, CarFront } from 'lucide-react'
import type { SavedPlaceItem } from '../hooks/useSavedPlaces'

type SavedTourCardProps = {
  place: SavedPlaceItem
  onExplore: () => void
  onRemove: () => void
}

const IMAGE_CLIP_PATH =
  'polygon(304px 454px, 304px 454px, 303.803668px 456.433012px, 303.235264px 458.741056px, 302.325676px 460.893244px, 301.105792px 462.858688px, 299.6065px 464.6065px, 297.858688px 466.105792px, 295.893244px 467.325676px, 293.741056px 468.235264px, 291.433012px 468.803668px, 289px 469px, 15px 469px, 15px 469px, 12.56692239px 468.803668px, 10.25884032px 468.235264px, 8.10663693px 467.325676px, 6.14119536px 466.105792px, 4.39339875px 464.6065px, 2.89413024px 462.858688px, 1.67427297px 460.893244px, 0.76471008px 458.741056px, 0.19632471px 456.433012px, 2.4833328970406E-31px 454px, 0px 55px, 0px 55px, 0.19632471px 52.5669151px, 0.76471008px 50.2588288px, 1.67427297px 48.1066237px, 2.89413024px 46.1411824px, 4.39339875px 44.3933875px, 6.14119536px 42.8941216px, 8.10663693px 41.6742673px, 10.25884032px 40.7647072px, 12.56692239px 40.1963239px, 15px 40px, 129px 40px, 129px 40px, 131.433012px 39.8036761px, 133.741056px 39.2352928px, 135.893244px 38.3257327px, 137.858688px 37.1058784px, 139.6065px 35.6066125px, 141.105792px 33.8588176px, 142.325676px 31.8933763px, 143.235264px 29.7411712px, 143.803668px 27.4330849px, 144px 25px, 144px 15px, 144px 15px, 144.196332px 12.56692239px, 144.764736px 10.25884032px, 145.674324px 8.10663693px, 146.894208px 6.14119536px, 148.3935px 4.39339875px, 150.141312px 2.89413024px, 152.106756px 1.67427297px, 154.258944px 0.76471008px, 156.566988px 0.19632471px, 159px 2.4833328970406E-31px, 289px 0px, 289px 0px, 291.433012px 0.19632471px, 293.741056px 0.76471008px, 295.893244px 1.67427297px, 297.858688px 2.89413024px, 299.6065px 4.39339875px, 301.105792px 6.14119536px, 302.325676px 8.10663693px, 303.235264px 10.25884032px, 303.803668px 12.56692239px, 304px 15px, 304px 454px)'

/**
 * Same private-tour card design as Van Tours / Private Tour listing.
 */
export default function SavedTourCard({ place, onExplore, onRemove }: SavedTourCardProps) {
  return (
    <article className="relative flex h-[469px] w-[304px] shrink-0 flex-col overflow-hidden rounded-[14px] bg-[#efeeee] shadow-[0_2px_7px_rgba(103,89,89,0.25)]">
      <div className="absolute left-[16px] top-[12px] z-10">
        <span className="font-host text-[15px] font-semibold leading-none tracking-[-0.01em] text-[#c56c3d]">
          {place.duration}
        </span>
      </div>

      <div
        className="relative h-[336px] shrink-0 overflow-hidden"
        style={{ clipPath: IMAGE_CLIP_PATH }}
      >
        <img
          src={place.image}
          alt={place.title}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />

        <button
          type="button"
          aria-label={`Remove ${place.title || 'tour'} from saved places`}
          onClick={(event) => {
            event.stopPropagation()
            onRemove()
          }}
          className="absolute right-2.5 top-2.5 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/95 shadow-[0_1px_3px_rgba(0,0,0,0.12)] transition-transform duration-200 hover:scale-105 active:scale-95"
        >
          <Bookmark className="h-[15px] w-[15px] fill-[#EF4444] stroke-[#EF4444] stroke-[2.1] text-[#EF4444]" />
        </button>
      </div>

      <div className="flex flex-1 flex-col justify-between bg-white px-[12px] pb-[12px] pt-[10px]">
        <div>
          <div className="mb-2.5 flex items-center gap-2 text-[12px] leading-none text-[#202020]">
            <span className="inline-flex items-center gap-1">
              <Camera className="h-3 w-3 fill-current stroke-[2.1]" />
              Sightseeing
            </span>
            <span className="text-[#c26f40]">•</span>
            <span className="inline-flex items-center gap-1">
              <CarFront className="h-3 w-3 fill-current stroke-[2.1]" />
              Transfers
            </span>
          </div>

          <h3 className="font-host text-[15px] font-semibold leading-[1.23] tracking-[-0.02em] text-[#111111]">
            {place.title || 'Saved place'}
          </h3>
        </div>

        <div className="mt-3 flex items-end justify-between gap-3">
          <div>
            <p className="font-host text-[12px] leading-none text-[#666666]">Starting From</p>
            <div className="mt-1 flex items-end gap-1">
              <span className="font-host text-[19px] font-medium leading-none tracking-[-0.03em] text-[#555555]">
                €{place.price}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              onExplore()
            }}
            className="flex h-[42px] items-center justify-center rounded-[15px] border border-[#B8B8B8] bg-[#ff7a29] px-[24px] font-host text-[13px] font-semibold tracking-[-0.01em] text-white shadow-[0_2px_4px_rgba(0,0,0,0.12)] transition-all hover:bg-[#e66c22] active:scale-95"
          >
            Explore Trip
          </button>
        </div>
      </div>
    </article>
  )
}
